/**
 * Client for the public challenge API (/api/challenges/*).
 *
 * Identity is anonymous: a random token generated once and kept in localStorage,
 * sent with every submission. The chosen username (default empty = "Anonymous") is
 * also local and travels with submissions. All calls are best-effort — network
 * errors are swallowed so the app keeps working fully offline.
 */
import {
    CurrentChallengesResponse, RecentChallengesResponse, LeaderboardResponse, SubmitResponse,
} from '../shared/apiTypes.js';

const TOKEN_KEY = 'grid_user_token';
const USERNAME_KEY = 'grid_username';

function getToken(): string {
    let t = localStorage.getItem(TOKEN_KEY);
    if (!t) {
        t = crypto.randomUUID();
        localStorage.setItem(TOKEN_KEY, t);
    }
    return t;
}

/** Current username, or '' when unset (rendered as "Anonymous" in the leaderboard). */
function getUsername(): string {
    return localStorage.getItem(USERNAME_KEY) ?? '';
}

function setUsername(name: string): void {
    localStorage.setItem(USERNAME_KEY, name);
}

async function getJson<T>(path: string): Promise<T | null> {
    try {
        const res = await fetch(`/api${path}`, { headers: { 'X-User-Token': getToken() } });
        if (!res.ok) return null;
        return (await res.json()) as T;
    } catch {
        return null; // offline / backend down — caller treats null as "no challenges"
    }
}

export const ApiClient = {
    getToken,
    getUsername,
    setUsername,

    getCurrentChallenges: (): Promise<CurrentChallengesResponse | null> =>
        getJson('/challenges/current'),

    getRecentChallenges: (limit = 14): Promise<RecentChallengesResponse | null> =>
        getJson(`/challenges/recent?limit=${limit}`),

    getLeaderboard: (challengeId: number): Promise<LeaderboardResponse | null> =>
        getJson(`/challenges/${challengeId}/leaderboard`),

    /** Submit the current solution block. Returns null on network failure. */
    async submitSolution(challengeId: number, solutionData: string): Promise<SubmitResponse | null> {
        const username = getUsername();
        try {
            const res = await fetch(`/api/challenges/${challengeId}/solutions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_token: getToken(),
                    solution_data: solutionData,
                    ...(username ? { username } : {}),
                }),
            });
            return (await res.json().catch(() => null)) as SubmitResponse | null;
        } catch {
            return null;
        }
    },
};
